import { useEffect, useState, type RefObject } from "react";

import styles from "./AppShell.module.css";

import { Button } from "@/components/Button";
import { ArrowUpIcon } from "@/components/icons";

/**
 * "Back to top" for the page content. The page scrolls inside the shell's
 * content section, not the window, so this watches that element. It shows
 * once the reader is about one screen down, and re-checks on every route
 * change so a new page starts from its own scroll position.
 */
export function BackToTop({
  target,
  routeKey,
}: {
  target: RefObject<HTMLElement | null>;
  routeKey: string;
}) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = target.current;
    if (!el) return;
    const update = () => setShown(el.scrollTop > el.clientHeight);
    update();
    el.addEventListener("scroll", update, { passive: true });
    return () => el.removeEventListener("scroll", update);
  }, [target, routeKey]);

  if (!shown) return null;

  const toTop = () => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    target.current?.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };

  return (
    <Button
      className={styles.backToTop}
      onClick={toTop}
      aria-label="Back to top"
      title="Back to top"
    >
      <ArrowUpIcon />
    </Button>
  );
}
